// Single document → one claim (note-to-claim) — wired to the live upstream via the proxy.
// Contract discovered from the API:
//   upload    POST  multipart form: file, npi_number  → { note: { id, status, ... } }
//             (upload alone creates + queues the note; no separate /jobs call for single-doc)
//   poll      GET   /note-to-claim/notes/{id}          → { note } (status: queued|processing|ready|failed)
//   delete    DELETE /note-to-claim/notes/{id}
//   reprocess POST  /note-to-claim/notes/{id}/reprocess
import { api } from "@/lib/api";

const BASE = "/api/edi/note-to-claim";

export const MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB cap
export const ACCEPTED = ".pdf,image/png,image/jpeg,image/jpg";

export function validateDocFile(file) {
  if (!file) return "No file selected.";
  const okType = /pdf$/i.test(file.type) || /^image\/(png|jpe?g)$/i.test(file.type) ||
    /\.(pdf|png|jpe?g)$/i.test(file.name);
  if (!okType) return "Unsupported file type. Upload a PDF, PNG or JPG.";
  if (file.size > MAX_FILE_BYTES) return "File is too large. Maximum is 50 MB.";
  if (file.size === 0) return "That file is empty.";
  return null;
}

// §3.2 — client-side de-dup. There is NO server-side de-dup: every /jobs or /upload
// call creates a NEW visit. We fingerprint the file set + NPI (see fingerprintUpload /
// lookupUpload below) and refuse to submit the same set twice unless the caller forces it.
// We also never auto-retry a POST (axios does not); callers check /notes instead.
export class DuplicateUploadError extends Error {
  constructor(prev) { super("This file set was already submitted."); this.name = "DuplicateUploadError"; this.prev = prev; }
}

// Uploads a single document and returns the created note (already queued for processing).
export async function uploadDocument(file, npiNumber, { onProgress, signal, force, clientId } = {}) {
  const fp = await fingerprintUpload([file], npiNumber);
  if (!force) { const prev = lookupUpload(fp); if (prev) throw new DuplicateUploadError(prev); }
  const form = new FormData();
  form.append("file", file, file.name);
  form.append("npi_number", npiNumber);
  const { data } = await api.post(`${BASE}/upload`, form, {
    signal,
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  const note = data?.note || null;
  if (note?.id) recordUpload(fp, { noteId: note.id, filename: file.name, clientId, npi: npiNumber });
  return note;
}

// Creates a multi-document Note-to-Claim job (≤10 docs) and returns the created
// note (single visit_id). Only medical docs feed coding; cards are read for insurance.
export async function createNoteJob(files, npiNumber, { onProgress, signal, force, clientId } = {}) {
  const fp = await fingerprintUpload(files, npiNumber);
  if (!force) { const prev = lookupUpload(fp); if (prev) throw new DuplicateUploadError(prev); }
  const form = new FormData();
  (files || []).forEach((f) => form.append("files", f, f.name));
  form.append("npi_number", npiNumber);
  const { data } = await api.post(`${BASE}/jobs`, form, {
    signal,
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  const note = data?.note || null;
  if (note?.id) recordUpload(fp, { noteId: note.id, filename: files?.[0]?.name, clientId, npi: npiNumber });
  return note;
}

// ── Smart (multi-part) upload ───────────────────────────────────────────────
// One intake path that auto-detects & splits one or more files (1–10, PDFs/images,
// ≤25 MB each) by patient + date of service into N visits, with an error queue for
// segments that can't be auto-created. Returns { batch_id, status } immediately; poll
// the batch for progress. No patient is pre-selected — the same upload handles a single
// document (→ 1 visit) and a bundle holding many patients/dates (→ many visits).
export async function startSplitUpload(files, npiNumber, { taxonomy_code, onProgress, signal } = {}) {
  const list = Array.from(files || []).filter(Boolean);
  if (!list.length) throw new Error("No files to send.");
  const form = new FormData();
  list.forEach((f) => form.append("files", f, f.name));
  form.append("npi_number", npiNumber);
  if (taxonomy_code) form.append("taxonomy_code", taxonomy_code);
  const { data } = await api.post(`${BASE}/notes/split-upload`, form, {
    signal,
    onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
  });
  return data; // { batch_id, status:"processing" }
}

// Poll a single smart-upload batch. Shape: { id, filename, status (processing|done|failed),
// num_pages, num_visits, created_count, failed_count, segments[], created_at, updated_at, error }.
export async function getSplitBatch(batchId, { signal } = {}) {
  const { data } = await api.get(`${BASE}/notes/split-batches/${batchId}`, { signal });
  return data || null;
}

// List recent smart-upload batches for the "Multi-part uploads" tracker (newest first).
export async function listNoteSplitBatches({ client_id, limit = 50, view, etag } = {}) {
  const { data } = await api.get(`${BASE}/notes/split-batches`, { params: { client_id, limit, ...(view ? { view } : {}) }, ...(etag ? { etag: true } : {}) });
  return Array.isArray(data?.batches) ? data.batches : [];
}

// Resolve a FAILED segment from the error queue by assigning a roster MRN + a DOS.
// The stored pages are rebuilt into a visit (no re-upload). Returns the updated batch summary.
export async function resolveSplitSegment(batchId, segIndex, { mrn, dos }) {
  const { data } = await api.post(`${BASE}/notes/split-batches/${batchId}/segments/${segIndex}/resolve`, { mrn, dos });
  return data || null;
}

export async function getNote(noteId, { signal } = {}) {
  const { data } = await api.get(`${BASE}/notes/${noteId}`, { signal });
  return data?.note || null;
}

// ── Delta sync (RevQ Delta-Sync guide) ──────────────────────────────────────
// Source of truth for *what* changed: ids + volatile queue fields since `since`.
// Tiny payload. Shape: { changes:[{id,status,claim_status,signed,match_status,
// codes_stale,updated_at,state_version,deleted?}], count, now, next_since? }.
export async function notesChanges(clientId, since) {
  if (!clientId) return { changes: [], now: since || "" };
  const params = { client_id: String(clientId) };
  if (since) params.since = since;
  const { data } = await api.get(`${BASE}/notes/changes`, { params });
  return data || { changes: [], now: since || "" };
}

// Catch-up fetch: full list-shaped rows for notes changed since `since`
// (same shape the queue window already renders). Used to merge only the deltas.
export async function listNotesChangedSince(clientId, since, limit = 300) {
  if (!clientId || !since) return [];
  const { data } = await api.get(`${BASE}/notes`, { params: { client_id: String(clientId), updated_since: since, limit } });
  return data?.notes || [];
}

// Live worker-pool snapshot for the Note-to-Claim charge-capture pipeline.
export async function getNoteQueueHealth({ signal } = {}) {
  const { data } = await api.get(`${BASE}/queue-health`, { signal });
  return data;
}

export async function deleteNote(noteId) {
  await api.delete(`${BASE}/notes/${noteId}`);
}

// §1.6 — add a patient straight to a client's roster (no visit). 409 → { duplicates[] }.
export async function addRosterPatient(clientId, body) {
  const { data } = await api.post(`${BASE}/clients/${clientId}/roster-patients`, body || {});
  return data; // { added, patient{ mrn, ... } }
}
// USA address typeahead (keyless geocoder). q >= 3 chars; debounce ~300ms upstream.
export async function addressSearch(q, state) {
  const { data } = await api.get(`/api/edi/televisit/address-search`, { params: { q, ...(state ? { state } : {}) } });
  return Array.isArray(data?.suggestions) ? data.suggestions : [];
}
export async function getStaleCount(clientId) {
  if (!clientId) return 0;
  const { data } = await api.get(`${BASE}/notes`, { params: { client_id: clientId, codes_stale: true, count_only: true } });
  return data?.total ?? 0;
}

// Work-queue badge counts for a client in ONE lightweight call (no row bodies; carries an ETag).
// Replaces the "fetch a page just to read a count" anti-pattern. Contract §6 → /notes/summary.
// Shape: { needs_payer_id, unsigned, ready_to_claim, codes_stale, by_status: {...} }
export async function getNotesSummary(clientId) {
  if (!clientId) return {};
  const { data } = await api.get(`${BASE}/notes/summary`, { params: { client_id: String(clientId) }, etag: true });
  return data || {};
}

export async function reprocessNote(noteId) {
  const { data } = await api.post(`${BASE}/notes/${noteId}/reprocess`);
  return data?.note || null;
}

// Whole-claim save. POST /notes/{id}/claim persists the biller-editable claim snapshot
// (providers, facility, payer, ordered diagnoses, service_lines w/ modifiers+dx, prior_auth)
// in one call, sets claim_status=queued, and appends a before/after claim_audit entry.
// An empty body composes the claim from the engine output. Returns the refreshed note.
//
// IMPORTANT: the save API reads the NM1 provider loops as top-level `rendering` / `billing` /
// `referring` keys. The GET snapshot (and therefore our edit state) uses the `*_provider` names.
// If we post `*_provider`, the server silently ignores them and the edit is dropped (verified).
// So we map them to the schema keys here. "" clears a field (also verified).
function toClaimPayload(claim) {
  if (!claim) return {};
  const c = JSON.parse(JSON.stringify(claim));
  const rp = c.rendering_provider || c.provider || {};
  const bp = c.billing_provider || {};
  const fp = c.referring_provider || {};
  c.rendering = { name: rp.name || "", npi: rp.npi || "", taxonomy_code: rp.taxonomy_code || "" };
  c.billing = { name: bp.name || "", npi: bp.npi || "", tax_id: bp.tax_id || "", address: bp.address ?? null };
  c.referring = { name: fp.name || "", npi: fp.npi || "" };
  delete c.rendering_provider;
  delete c.provider;
  delete c.billing_provider;
  delete c.referring_provider;
  return c;
}

export async function saveClaim(noteId, claim) {
  const { data } = await api.post(`${BASE}/notes/${noteId}/claim`, toClaimPayload(claim));
  return data?.note || null;
}

// ── Signed visits ──────────────────────────────────────────────────────────
// A visit's claim can only be generated once it is signed. Sign is idempotent and
// stamps signed/signed_at/signed_by + a "Visit signed" change-log entry. Unsign
// clears it (use before editing a signed visit).
export async function signNote(noteId) {
  const { data } = await api.post(`${BASE}/notes/${noteId}/sign`);
  return data; // { signed:true, already, signed_at, signed_by{login_id,name,email,ip} }
}
export async function unsignNote(noteId) {
  const { data } = await api.post(`${BASE}/notes/${noteId}/unsign`);
  return data; // { signed:false, already }
}

// Generate the claim from the engine output (empty body composes it). Sets
// claim_status=queued. Returns 400 "Visit/Note needs to be signed first" when unsigned.
export async function generateClaim(noteId) {
  const { data } = await api.post(`${BASE}/notes/${noteId}/claim`, {});
  return data?.note || data || null;
}

// Box 21 reorder. PATCH /notes/{id}/diagnoses { ordered_codes } rewrites icd_codes order.
// It only REORDERS existing codes — codes not already present are silently dropped, so it
// can't add a typed diagnosis (that rides the claim snapshot via saveClaim). Returns the note.
export async function reorderDiagnoses(noteId, orderedCodes) {
  const { data } = await api.patch(`${BASE}/notes/${noteId}/diagnoses`, { ordered_codes: orderedCodes });
  return data?.note || null;
}

// Add a single diagnosis to the note's Box 21. POST /notes/{id}/diagnoses { code }.
export async function addDiagnosis(noteId, code) {
  const { data } = await api.post(`${BASE}/notes/${noteId}/diagnoses`, { code });
  return data?.note || data || null;
}

// ICD-10 search for the add-diagnosis field. Returns the raw tree { fy, nodes[], ... }.
export async function icdSearch(q, { dos, signal } = {}) {
  const { data } = await api.get(`${BASE}/icd/search`, { params: { q, limit: 50, ...(dos ? { dos } : {}) }, signal });
  return data || { nodes: [] };
}


// Runs the 837P scrubber → pass/fail + a list of rule findings.
// The upstream requires a JSON body (an empty object runs against the built claim);
// posting with no body returns 422 "Field required".
export async function scrubNote(noteId) {
  const { data } = await api.post(`${BASE}/notes/${noteId}/scrub`, {});
  return data;
}

// Normalizer for the real scrub payload. The upstream returns rule *counts*
// (`passed` / `failed` are numbers, not booleans), hard rule breaks under
// `failures[]`, soft ones under `soft_failures[]`, plus a `score` and the
// assembled `claim_built`. Older/simpler shapes are still accepted defensively.
export function normalizeScrub(raw) {
  if (!raw) return null;
  const s = raw.scrub || raw.result_detail || raw;

  const toFinding = (f, fallbackSev) => {
    if (typeof f === "string") return { severity: fallbackSev, code: null, message: f };
    const sev = String(f.severity || f.level || (f.error ? "error" : f.warning ? "warning" : fallbackSev)).toLowerCase();
    return {
      severity: /err|fail|crit|hard/.test(sev) ? "error" : /warn|soft/.test(sev) ? "warning" : fallbackSev,
      code: f.scrubber_code || f.code || f.rule || f.rule_id || f.id || null,
      message: f.message || f.reason || f.msg || f.description || f.text || f.detail || f.rule_name || "",
    };
  };

  const hard = Array.isArray(s.failures) ? s.failures.map((f) => toFinding(f, "error")) : [];
  const soft = Array.isArray(s.soft_failures) ? s.soft_failures.map((f) => toFinding(f, "warning")) : [];
  const legacyRaw = s.findings || s.rules || s.issues || s.rule_hits || (Array.isArray(s.errors) ? s.errors : null);
  const legacy = Array.isArray(legacyRaw) ? legacyRaw.map((f) => toFinding(f, "info")) : [];

  const findings = [...hard, ...soft, ...legacy];
  const errors = findings.filter((f) => f.severity === "error").length;
  const warnings = findings.filter((f) => f.severity === "warning").length;

  // Prefer the upstream's hard-failure count; fall back to derived errors.
  const failedCount =
    typeof s.failed_total === "number" ? s.failed_total :
    typeof s.failed === "number" ? s.failed : errors;

  // Legacy boolean flag, if present, still wins.
  let passed = s.is_clean ?? s.clean;
  if (passed == null && typeof s.pass === "boolean") passed = s.pass;
  if (passed == null) passed = failedCount === 0 && errors === 0;

  return {
    passed: !!passed,
    findings,
    errors,
    warnings,
    score: typeof s.score === "number" ? s.score : null,
    claimBuilt: s.claim_built || null,
  };
}

export function isNoteDone(note) {
  const s = note?.status;
  return s === "ready" || s === "failed" || s === "error";
}

// ── Interim status: drive the step list from the real note.stage (no streaming) ──
// The worker writes `stage` + `stage_message` as it advances; we poll and render.
export const NOTE_STAGE_ORDER = [
  "queued", "ocr", "extracting", "matching", "visit",
  "icd_validate", "shawl", "payer", "modifiers",
  "combinations", "rules", "billing_output", "done",
];
// The worker sometimes emits an internal name — map before matching.
export const NOTE_STAGE_ALIAS = { validating: "visit", classifying: "extracting" };

// The four UI groups, each carrying the real stage keys in order.
export const NOTE_PHASES = [
  {
    t: "Encounter & context",
    steps: [
      { key: "ocr", t: "Reading & OCR-ing the document(s)", s: "Pulling text, layout and context from every page…" },
      { key: "extracting", t: "Identifying patient, date of service & place of service", s: "Extracting the who / when / where from the document…" },
      { key: "matching", t: "Matching the patient", s: "Matching patient — live EHI, then your roster…" },
      { key: "visit", t: "Classifying the visit", s: "Classifying visit type & key encounter events…" },
    ],
  },
  {
    t: "Diagnosis · ICD-10",
    steps: [
      { key: "icd_validate", t: "Validating documented diagnoses", s: "Checking provenance & diagnosis-pairing indicators…" },
    ],
  },
  {
    t: "Procedure · CPT",
    steps: [
      { key: "shawl", t: "Screening CPTs against SHAWL", s: "Age / gender / client / taxonomy screening…" },
      { key: "payer", t: "Checking payer-ID nuances & pre-auth", s: "Payer-specific rules and prior-auth checks…" },
      { key: "modifiers", t: "Evaluating anatomical-modifier dependency", s: "Resolving required anatomical modifiers…" },
    ],
  },
  {
    t: "Billing optimisation",
    steps: [
      { key: "combinations", t: "Cross-referencing CPT combinations", s: "Checking billable CPT combinations…" },
      { key: "rules", t: "Applying inclusion & exclusion rules", s: "Client / provider rules applied…" },
      { key: "billing_output", t: "Composing the ideal billing output", s: "Assembling the final claim…" },
    ],
  },
];

// Current stage index into NOTE_STAGE_ORDER, from a (possibly interim) note.
export function noteStageIndex(note) {
  const status = note?.status || "queued";
  if (status === "ready") return NOTE_STAGE_ORDER.length - 1; // "done"
  const raw = note?.stage || "queued";
  const stage = NOTE_STAGE_ALIAS[raw] || raw;
  const idx = NOTE_STAGE_ORDER.indexOf(stage);
  return idx < 0 ? 0 : idx;
}

// Plain-English current coding step for a note revQ is still working on.
export function codingStep(note) {
  const steps = NOTE_PHASES.flatMap((p) => p.steps);
  const raw = note?.stage || "queued", key = NOTE_STAGE_ALIAS[raw] || raw;
  const i = steps.findIndex((s) => s.key === key);
  const label = note?.stage_message || (i >= 0 ? steps[i].t : key === "queued" ? "Waiting in line to start" : "Working on it");
  return { label, step: i >= 0 ? i + 1 : 0, total: steps.length };
}

// State of a single step key relative to the note's current stage: done | run | pending.
export function noteStepState(stepKey, note) {
  const idx = NOTE_STAGE_ORDER.indexOf(stepKey);
  const cur = noteStageIndex(note);
  if (idx < cur) return "done";
  if (idx === cur) return "run";
  return "pending";
}

export function isNoteFailed(note) {
  const s = note?.status;
  return s === "failed" || s === "error";
}

// Extracts one or more claims from a note. A batch job may return `claims[]` (or
// per-document coding under `documents[]`); a single note collapses to one claim.
// Defensive — the exact shape isn't pinned, so fall back to note-level coding.
export function normalizeClaims(note) {
  if (!note) return [];
  const base = (o, i) => ({
    id: o.id || o.claim_id || o.doc_id || o.document_id || `claim-${i}`,
    label: o.filename || o.document_name || o.patient_name || `Claim ${i + 1}`,
    patient_name: o.patient_name ?? note.patient_name ?? null,
    patient_dob: o.patient_dob ?? note.patient_dob ?? null,
    provider_name: o.provider_name ?? note.provider_name ?? null,
    provider_npi: o.provider_npi ?? note.provider_npi ?? null,
    payer_name: o.payer_name ?? note.payer_name ?? null,
    member_id: o.member_id ?? note.member_id ?? null,
    place_of_service: o.place_of_service ?? note.place_of_service ?? null,
    pos_code: o.pos_code ?? note.pos_code ?? null,
    cpt_count: o.cpt_count,
    icd_count: o.icd_count,
    cpt_codes: Array.isArray(o.cpt_codes) ? o.cpt_codes : [],
    icd_codes: Array.isArray(o.icd_codes) ? o.icd_codes : [],
  });
  const list =
    (Array.isArray(note.claims) && note.claims.length && note.claims) ||
    (Array.isArray(note.documents) && note.documents.some((d) => d.cpt_codes || d.icd_codes) && note.documents) ||
    null;
  if (list) return list.map(base);
  return [base(note, 0)];
}

// Documents attached to a (batch) note, for the source-note rail.
export function noteDocuments(note) {
  const docs = note?.documents || note?.files || note?.docs;
  if (!Array.isArray(docs)) return [];
  return docs.map((d, i) => ({
    id: d.id || d.doc_id || d.document_id || `doc-${i}`,
    name: d.filename || d.document_name || d.name || `Document ${i + 1}`,
    kind: d.classification || d.type || d.kind || null,
  }));
}

// ───────────────────────── one-send-per-visit plumbing ─────────────────────────
// A multi-document job (one visit) uses /jobs; a single document uses /upload.
export async function sendClaimUpload({ files, npi, mode, onProgress, signal }) {
  const list = Array.from(files || []).filter(Boolean);
  if (!list.length) throw new Error("No files to send.");
  if (mode === "visit" || list.length > 1) return createNoteJob(list, npi, { onProgress, signal });
  return uploadDocument(list[0], npi, { onProgress, signal });
}

// Fingerprint = SHA-256 of every file's bytes (sorted) + the billing NPI. Same files +
// same provider ⇒ same visit; a changed file or a different provider is a new, legit visit.
async function sha256Hex(buf) {
  const d = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
export async function fingerprintUpload(files, npi) {
  const list = Array.from(files || []).filter(Boolean);
  const hashes = [];
  for (const f of list) hashes.push(await sha256Hex(await f.arrayBuffer()));
  hashes.sort();
  return sha256Hex(new TextEncoder().encode(`${npi || ""}::${hashes.join("|")}`));
}

// Upload ledger — remembers past uploads (fingerprint → visit) across refreshes so
// re-dropping yesterday's file is caught. Kept small (last 200).
const LEDGER_KEY = "revq.ntc.uploads";
function loadLedger() {
  try { return JSON.parse(localStorage.getItem(LEDGER_KEY) || "{}") || {}; } catch { return {}; }
}
export function lookupUpload(fp) {
  if (!fp) return null;
  const e = loadLedger()[fp];
  return e && e.noteId ? e : null;
}
export function recordUpload(fp, { noteId, filename, clientId, npi } = {}) {
  if (!fp || !noteId) return;
  try {
    const l = loadLedger();
    l[fp] = { noteId, filename: filename || "", clientId: clientId ?? null, npi: npi ?? null, ts: Date.now() };
    const keys = Object.keys(l);
    if (keys.length > 200) { keys.sort((a, b) => (l[a].ts || 0) - (l[b].ts || 0)); keys.slice(0, keys.length - 200).forEach((k) => delete l[k]); }
    localStorage.setItem(LEDGER_KEY, JSON.stringify(l));
  } catch { /* storage full / disabled — dedup simply won't persist */ }
}

// No blind re-sending: before re-uploading after a timeout/error, look for a visit that
// was JUST created with the same provider + first file (name + size). Returns it if found.
export async function findRecentVisit({ npi, filename, size, clientId, windowMs = 10 * 60 * 1000 }) {
  try {
    const { data } = await api.get(`${BASE}/notes`, { params: { client_id: clientId, limit: 25, skip: 0 } });
    const now = Date.now();
    return (data?.notes || []).find((n) => {
      const created = n.created_at ? new Date(n.created_at).getTime() : 0;
      if (now - created > windowMs) return false;
      if (npi && n.npi_number && String(n.npi_number) !== String(npi)) return false;
      const nameOk = filename && n.filename && n.filename === filename;
      const sizeOk = size == null || n.file_size == null || Number(n.file_size) === Number(size);
      return nameOk && sizeOk;
    }) || null;
  } catch { return null; }
}

export async function rematchNote(noteId) {
  const { data } = await api.post(`${BASE}/notes/${noteId}/rematch`, {});
  return data?.note || data || null;
}

// Set / link the note's patient. Body: { first_name, last_name, date_of_birth, gender, mrn? }.
// The server re-matches against the roster, so an "added" patient that already exists links
// to the existing record (status "matched") instead of duplicating. Returns { patient, match }.
export async function setNotePatient(noteId, body) {
  const { data } = await api.patch(`${BASE}/notes/${noteId}/patient`, body || {});
  return data || null;
}

// Client patient directory — same endpoint as the top search. Empty q → the first capped batch
// the server returns; a q filters it. The endpoint has NO server-side paging (limit/offset are
// ignored), so callers page client-side over the returned set. Returns { results, demo, source }.
export async function listClientPatients(clientId, q) {
  const params = { client_id: clientId };
  const term = (q || "").trim();
  if (term) params.q = term;
  const { data } = await api.get(`${BASE}/pg-patient-search`, { params });
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    demo: !!data?.demo,
    source: data?.source || "",
  };
}

// Patient comments (expanded row). Existing upstream endpoints, consumed as-is.
export async function listPatientComments(clientId, chartId, includeRemoved = false) {
  const params = { client_id: clientId, chart_id: chartId };
  if (includeRemoved) params.include_removed = true;
  const { data } = await api.get("/api/edi/televisit/patient-comments", { params });
  return { comments: Array.isArray(data?.comments) ? data.comments : [], total: data?.total ?? 0 };
}
export async function postPatientComment(clientId, chartId, text) {
  const { data } = await api.post("/api/edi/televisit/patient-comments", { client_id: clientId, chart_id: chartId, text });
  return data;
}
export async function patchPatientComment(commentId, removed) {
  const { data } = await api.patch(`/api/edi/televisit/patient-comments/${commentId}`, { removed });
  return data;
}

// Pre-built "most-active" patient panel (televisit/patient-summary). Each row is ready to render and
// carries chart_uuid. Optional filter: unsigned | telemed_today | new_documents | has_comments.
// Returns { patients, total, filter, fallback }.
export async function listPatientSummary(clientId, { limit = 50, filter = "" } = {}) {
  const params = { client_id: clientId, limit };
  if (filter) params.filter = filter;
  const { data } = await api.get("/api/edi/televisit/patient-summary", { params });
  return {
    patients: Array.isArray(data?.patients) ? data.patients : [],
    total: data?.total ?? null,
    filter: data?.filter ?? null,
    fallback: !!data?.fallback,
  };
}

// Patient search (live chart DB first, then roster, then imported), scoped to the client.
export async function searchPatients(clientId, q) {
  if (!q || !q.trim()) return [];
  const { data } = await api.get(`${BASE}/pg-patient-search`, { params: { client_id: clientId, q: q.trim() } });
  return Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : (data?.patients || []);
}

// Full patient search payload for the Match panel — returns { bound, source, results }.
// Search-only: callers gate on q ≥ 2 chars. Never throws to the UI (returns empty on trouble).
export async function searchPatientsFull(clientId, q) {
  const term = (q || "").trim();
  if (term.length < 2) return { bound: true, source: "", results: [] };
  try {
    const { data } = await api.get(`${BASE}/pg-patient-search`, { params: { client_id: clientId, q: term } });
    return { bound: data?.bound !== false, source: data?.source || "", results: Array.isArray(data?.results) ? data.results : [] };
  } catch { return { bound: true, source: "", results: [] }; }
}

// Payer type-ahead over the client's migrated payer master → [{ payer_name, payer_id, claim_filing_indicator }].
export async function manualPayers(clientId, q) {
  const params = { client_id: clientId }; const term = (q || "").trim(); if (term) params.q = term;
  const { data } = await api.get(`${BASE}/manual/payers`, { params });
  return Array.isArray(data?.items) ? data.items : [];
}

// The patient as the document reads it, per the standing rules:
//  matched → name + MRN; tentative → name + DOB (no MRN); no match → name/DOB read from
//  the document, no MRN, never blank when a name was read, never an invented MRN, and a
//  candidate is never shown as if it were the match.
export function patientView(note) {
  const n = note || {};
  const name = n.patient_name || [n.patient?.first_name, n.patient?.last_name].filter(Boolean).join(" ").trim();
  const dob = n.patient_dob || n.patient?.date_of_birth || null;
  const mrn = n.mrn || null;
  const st = n.match_status;
  const matched = st === "matched" || st === "preselected" || (!!mrn && st !== "tentative" && st !== "not_matched" && st !== "insufficient" && st !== "no_match");
  if (matched) return { badge: "matched", label: "Matched", name: name || "Patient", detail: mrn ? `MRN ${mrn}` : "", mrn };
  if (st === "tentative") return { badge: "tentative", label: "Tentative", name: name || (n.match_candidates?.[0]?.name) || "Patient", detail: dob ? `DOB ${dob}` : "", mrn: null };
  return { badge: "nomatch", label: "No match — read from document", name: name || (n.match_candidates?.[0]?.name) || "Patient not read", detail: dob ? `DOB ${dob}` : "no DOB on the document", mrn: null };
}

// True when a record isn't firmly matched to a patient yet (tentative or no-match) — i.e. still needs a person to match it.
export function needsPatientMatch(note) {
  return patientView(note).badge !== "matched";
}

// Coding/claim status for the queue chips. "failed" is failed (never shown as review).
export function claimStatusOf(note) {
  const s = note?.status;
  if (s === "failed" || s === "error") return "failed";
  if (s === "ready") return "ready";
  if (s === "queued") return "queued";
  return "processing";
}

// §3.4 Field-based search — each field is its own AND filter (server-side, blind-indexed).
// client_id MUST be a string. Returns { total, notes[] } newest first.
export async function searchNotes(fields) {
  const body = { ...fields };
  if (body.client_id != null) body.client_id = String(body.client_id);
  const { data } = await api.post(`${BASE}/notes/search`, body);
  return { total: data?.total ?? (data?.notes || []).length, notes: data?.notes || [] };
}

// Part F — Date-added filter. searchNotes returns newest-first by created_at, so we page
// until a row's added date is older than the range start, then keep the rows inside [from,to].
// `fields` carries the non-date filters (client_id, source, provider, tov, status, payer, seg).
// Returns { rows (newest-added first), hasOlder } — hasOlder is true if paging hit an older row.
export async function loadNotesByAdded(fields, { from, to } = {}) {
  const ymdLocal = (iso) => { const d = new Date(iso); if (isNaN(d)) return null; return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
  const CHUNK = 200, MAX = 5000;
  // The search endpoint rejects a client-only query ("fill in at least one field"). When no other
  // filter is set, list every note via GET /notes (also newest-added first) so Date-added works.
  const hasFilter = Object.keys(fields).some((k) => k !== "client_id" && fields[k] !== "" && fields[k] != null);
  const fetchPage = async (skip) => {
    if (hasFilter) { const { notes } = await searchNotes({ ...fields, skip, limit: CHUNK }); return notes; }
    const { data } = await api.get(`${BASE}/notes`, { params: { client_id: String(fields.client_id), limit: CHUNK, skip } });
    return data?.notes || [];
  };
  const rows = []; let skip = 0, hasOlder = false, fetched = 0;
  for (;;) {
    const notes = await fetchPage(skip);
    if (!notes.length) break;
    fetched += notes.length;
    let stop = false;
    for (const n of notes) {
      const c = ymdLocal(n.created_at);
      if (!c) continue;
      if (from && c < from) { hasOlder = true; stop = true; break; }
      if (to && c > to) continue;
      rows.push(n);
    }
    if (stop || notes.length < CHUNK || fetched >= MAX) break;
    skip += CHUNK;
  }
  return { rows, hasOlder };
}

// Part E — service locations for a client. Used to decide if a client is multi-location.
// Returns the raw list: [{ name, facility_name?, pos_code, address, npi }]. Cached per client by the caller.
export async function listServiceLocations(clientId) {
  const { data } = await api.get("/api/revq/service-locations", { params: { client_id: String(clientId) } });
  return data?.service_locations || [];
}

// §3.5 Recode from the edited note (Custom Note → Standard SOAP → visit note).
export async function recodeFromNote(noteId) {
  const { data } = await api.post(`${BASE}/notes/${noteId}/recode-from-note`);
  return data || null;
}

// Poll until AriaOne reports the recode done/failed. Read errors are skipped; "running" means we stopped waiting.
export async function waitForRecode(noteId, { timeoutMs = 120000, every = 2000, onNote } = {}) {
  const t0 = Date.now(); let note = null;
  while (Date.now() - t0 < timeoutMs) {
    await new Promise((r) => setTimeout(r, every));
    let n = null; try { n = await getNote(noteId); } catch { continue; }
    if (!n) continue;
    note = n; onNote?.(n);
    if (n.recode_status === "done" || n.recode_status === "failed") return { status: n.recode_status, note };
  }
  return { status: "running", note };
}

// Release 17 Oct: start the recode once, then poll. 409 = a recode is already running (wait on it,
// never re-POST) or the visit is still processing (status "busy"). Returns { status, note, already }.
export async function recodeAndWait(noteId, { timeoutMs = 180000, onNote } = {}) {
  let already = false;
  try { await recodeFromNote(noteId); } catch (e) {
    if (e?.response?.status !== 409) throw e;
    const n = await getNote(noteId).catch(() => null);
    if (!(n?.recode_status === "running" || n?.recode_status === "queued")) return { status: "busy", note: n, already: false };
    already = true; onNote?.(n);
  }
  const r = await waitForRecode(noteId, { timeoutMs, onNote });
  return { ...r, already };
}

// "Coded from …" — server provenance (coded_from.kind / .at). Null when the note has no record.
export function codedFromText(note) {
  const cf = note?.coded_from; if (!cf?.kind) return null;
  const at = cf.at ? new Date(cf.at) : null;
  const when = at && !isNaN(at) ? ` · ${at.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : "";
  if (cf.kind === "custom_note") return `Coded from your edited note${when}`;
  if (cf.kind === "standard_soap") return `Coded from the Standard SOAP${when}`;
  if (cf.kind === "pipeline") return `Coded from the original visit${when}`;
  return null;
}

// §3.6 Visit change log (newest first) + CSV export.
export async function getChangeLog(noteId) {
  const { data } = await api.get(`${BASE}/notes/${noteId}/change-log`);
  return Array.isArray(data?.entries) ? data.entries : [];
}
export async function downloadChangeLogCsv(noteId, visitId) {
  const resp = await api.get(`${BASE}/notes/${noteId}/change-log.csv`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = `${visitId || noteId}-change-log.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// §3.7 Match patient — link an existing roster MRN (exact), or add+link a new roster patient.
// rosterAdd throws a { duplicates } payload on 409 so the caller can offer "link that one".
export async function mapRosterPatient(noteId, mrn) {
  const { data } = await api.post(`${BASE}/notes/${noteId}/map-roster`, { mrn });
  return data || null;
}
// Link a patient resolved from the live EHI DB (pg-patient-search source: "postgres").
export async function mapPgPatient(noteId, mrn) {
  const { data } = await api.post(`${BASE}/notes/${noteId}/map-pg`, { mrn });
  return data || null;
}
export async function rosterAddPatient(noteId, body) {
  try {
    const { data } = await api.post(`${BASE}/notes/${noteId}/roster-add`, body || {});
    return { ok: true, data };
  } catch (e) {
    const detail = e?.response?.data?.detail;
    if (e?.response?.status === 409) return { ok: false, duplicates: detail?.duplicates || detail || [] };
    throw e;
  }
}

// ── Manage Insurance (primary + secondary) ──────────────────────────────────
// Availity payer master typeahead (q ≥ 2 chars). Prefer this over the curated /payers.
export async function availityPayers(q, limit = 20) {
  if (!q || q.trim().length < 2) return [];
  const { data } = await api.get(`${BASE}/availity-payers`, { params: { q: q.trim(), limit } });
  return Array.isArray(data?.payers) ? data.payers : [];
}

// The small curated/grounded payer list (has ids). Used as a fallback picker when the
// Availity master returns nothing in this environment.
export async function curatedPayers(clientId) {
  const { data } = await api.get(`${BASE}/payers`, { params: clientId ? { client_id: clientId } : {} });
  return (Array.isArray(data?.payers) ? data.payers : []).map((p) => ({ payer_id: String(p.id), name: p.name }));
}

// Set / confirm a payer for a sequence. body: { payer_id, payer_name?, member_id?, group_number?, sequence(1|2), teach_alias? }.
// Send payer_id:"" to update only member/group without changing the payer. Returns
// { insurance, sequence, visits_updated, payer_registered }.
export async function patchInsurance(noteId, body) {
  const { data } = await api.patch(`${BASE}/notes/${noteId}/insurance`, body || {});
  return data || null;
}

// Live EHI claims history for a matched patient. { bound, line_count, claims[] }; bound:false → no live connection.
export async function pgPatientHistory(clientId, mrn) {
  if (!clientId || !mrn) return { bound: false, claims: [] };
  const { data } = await api.get(`${BASE}/pg-patient-history`, { params: { client_id: clientId, mrn } });
  return data || { bound: false, claims: [] };
}


